import { useState, useCallback, useEffect } from "react";
import {
  AssignmentRule,
  GROUP_PRINCIPAL,
  TargetField,
} from "../../../../../application/domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import {
  GroupMap,
  buildGroupMap,
  Group,
} from "../../../../../application/domain/group/group";
import tenantManagementApiClient from "../../../../../application/apis/tenant-management/tenant-management-client";
import { useAuthContextV2 } from "../../../../../auth-state";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { SmartApplyR } from "../../route";
import { ValidationErrorVariables } from "@graffer-inc/form-schema";

export const useProcedureAssignmentSettingRuleSet = () => {
  const [rulesetName, setRulesetName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [targetFields, setTargetFields] = useState<TargetField[]>([]);
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [editingField, setEditingField] = useState<TargetField | undefined>(
    undefined,
  );
  const [groupMaps, setGroupMaps] = useState<GroupMap>({});
  const [isLoading, setIsLoading] = useState(false);

  const [nameErrorMessages, setNameErrorMessages] = useState<string[]>([]);
  const [descriptionErrorMessages, setDescriptionErrorMessages] = useState<
    string[]
  >([]);
  const [targetFieldsError, setTargetFieldsError] = useState<string>("");
  const [rulesError, setRulesError] = useState<string>("");

  const { auth } = useAuthContextV2();
  const history = useHistory();

  const GROUPS_PER_RULE = 100;

  const validateRulesetName = (name: string): boolean => {
    const nameErrors: string[] = [];
    const trimmedName = name.trim();
    if (!trimmedName) {
      nameErrors.push("名前は必須です。");
    } else if (trimmedName.length > ValidationErrorVariables.label.maxLength) {
      nameErrors.push(
        `${ValidationErrorVariables.label.maxLength}文字以内で入力してください。`,
      );
    }
    setNameErrorMessages(nameErrors);
    return nameErrors.length === 0;
  };

  const validateDescription = (desc: string): boolean => {
    const descErrors: string[] = [];
    const trimmedDesc = desc.trim();
    if (!trimmedDesc) {
      descErrors.push("説明は必須です。");
    } else if (
      trimmedDesc.length > ValidationErrorVariables.description.maxLength
    ) {
      descErrors.push(
        `${ValidationErrorVariables.description.maxLength}文字以内で入力してください。`,
      );
    }
    setDescriptionErrorMessages(descErrors);
    return descErrors.length === 0;
  };

  const validateTargetFields = (fields: TargetField[]): boolean => {
    let targetFieldsErr = "";
    if (!fields.length) {
      targetFieldsErr = "対象フィールドを1つ以上選択してください。";
    }
    setTargetFieldsError(targetFieldsErr);
    return targetFieldsErr === "";
  };

  const validateRules = (rules: AssignmentRule[]): boolean => {
    let rulesErr = "";
    if (!rules.length) {
      rulesErr = "振り分けルールを1つ以上設定してください。";
    }
    setRulesError(rulesErr);
    return rulesErr === "";
  };

  const handleRulesetNameChange = (name: string) => {
    setRulesetName(name);
    validateRulesetName(name);
  };

  const handleDescriptionChange = (desc: string) => {
    setDescription(desc);
    validateDescription(desc);
  };

  const validateFormData = () => {
    const isNameValid = validateRulesetName(rulesetName);
    const isDescValid = validateDescription(description);
    const areFieldsValid = validateTargetFields(targetFields);
    const areRulesValid = validateRules(rules);

    return isNameValid && isDescValid && areFieldsValid && areRulesValid;
  };

  // グループ関連のハンドラー
  const fetchGroupMaps = async (rules: AssignmentRule[], tenantId: string) => {
    setIsLoading(true);
    try {
      // groupPrincipalのassigneeのidを抽出し、new Set で重複を排除
      const groupIds = Array.from(
        new Set(
          rules
            .flatMap((rule) => rule.assignments)
            .filter(
              (assignment) => assignment.assignee.type === GROUP_PRINCIPAL,
            )
            .map((assignment) => assignment.assignee.id),
        ),
      );

      if (groupIds.length === 0) {
        setGroupMaps({});
        return;
      }

      const limit = GROUPS_PER_RULE;
      const allGroups: Group[] = [];

      // ページネーション処理
      const promises = Array.from(
        { length: Math.ceil(groupIds.length / limit) },
        (_, index) =>
          tenantManagementApiClient.fetchGroupsByOrganizationId(
            tenantId,
            index + 1,
            limit,
            {
              ids: groupIds,
            },
          ),
      );

      const results = await Promise.all(promises);
      results.forEach(({ data }) => {
        allGroups.push(...data);
      });

      setGroupMaps(buildGroupMap(allGroups));
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("グループ情報の取得に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 初期表示時のみグループ情報をフェッチする
    // rules変更時のフェッチは各画面で必要に応じて行う
    if (rules.length > 0) {
      fetchGroupMaps(rules, auth.orgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.orgId]);

  const fetchGroupsByIds = useCallback(
    async (groupIds: string[]) => {
      const { data } =
        await tenantManagementApiClient.fetchGroupsByOrganizationId(
          auth.orgId,
          1,
          GROUPS_PER_RULE,
          { ids: groupIds },
        );
      return data;
    },
    [auth.orgId],
  );

  const updateGroupMaps = useCallback(
    async (rule: AssignmentRule) => {
      const unfetchedGroupIds = rule.assignments.flatMap((assignment) => {
        if (
          assignment.assignee.type === GROUP_PRINCIPAL &&
          !groupMaps[assignment.assignee.id]
        ) {
          return [assignment.assignee.id];
        } else {
          return [];
        }
      });
      if (unfetchedGroupIds.length > 0) {
        setIsLoading(true);
        try {
          const groups = await fetchGroupsByIds(unfetchedGroupIds);
          setGroupMaps((prevGroupMaps) => ({
            ...prevGroupMaps,
            ...buildGroupMap(groups),
          }));
        } catch (e) {
          reportUnexpectedError(e);
          toast.error("グループ情報の取得に失敗しました。");
        } finally {
          setIsLoading(false);
        }
      }
    },
    [groupMaps, fetchGroupsByIds],
  );

  // targetFields のイベントハンドラー
  const handleAddField = useCallback((newField: TargetField) => {
    setTargetFields((prevFields) => {
      const updatedFields = [...prevFields, newField];
      validateTargetFields(updatedFields);
      return updatedFields;
    });
  }, []);

  const handleEditField = useCallback((updatedField: TargetField) => {
    setTargetFields((prevFields) => {
      const updatedFields = prevFields.map((field) =>
        field.elemId === updatedField.elemId ? updatedField : field,
      );
      validateTargetFields(updatedFields);
      return updatedFields;
    });
  }, []);

  const handleDeleteField = useCallback((elemId: string) => {
    setTargetFields((prevFields) => {
      const updatedFields = prevFields.filter(
        (field) => field.elemId !== elemId,
      );
      validateTargetFields(updatedFields);
      return updatedFields;
    });
  }, []);

  const handleSaveField = useCallback(
    (field: TargetField) => {
      // TODO: 各種バリデーション。項目タイプごとの選択肢の数の上限。選択肢の重複。
      if (editingField) {
        handleEditField(field);
      } else {
        handleAddField(field);
      }
      return true; // 保存成功時はtrueを返す
    },
    [editingField, handleEditField, handleAddField],
  );

  const isTargetFieldUsedInRules = useCallback(
    (elemId: string): boolean => {
      return rules.some((rule) =>
        rule.conditions.some((condition) => condition.id === elemId),
      );
    },
    [rules],
  );

  // rules のイベントハンドラー
  const handleDeleteRule = useCallback(
    (index: number) => {
      setRules((prevRules) => {
        const updatedRules = prevRules.filter((_, i) => i !== index);
        validateRules(updatedRules);
        return updatedRules;
      });
    },
    [setRules],
  );

  const handleSaveRule = useCallback(
    async (rule: AssignmentRule, index?: number) => {
      await updateGroupMaps(rule);
      if (index !== undefined) {
        setRules((prevRules) => {
          const newRules = [...prevRules];
          newRules[index] = rule;
          validateRules(newRules);
          return newRules;
        });
      } else {
        setRules((prevRules) => {
          const newRules = [...prevRules, rule];
          validateRules(newRules);
          return newRules;
        });
      }
    },
    [setRules, updateGroupMaps],
  );

  const handleCancel = useCallback(() => {
    // NOTE:離脱するとデータが失われるため、要望次第では確認ダイアログを表示する
    history.push(SmartApplyR.AssignmentSettingRulesets.link({}));
  }, [history]);

  return {
    rulesetName,
    setRulesetName: handleRulesetNameChange,
    description,
    setDescription: handleDescriptionChange,
    targetFields,
    setTargetFields,
    rules,
    setRules,
    editingField,
    setEditingField,
    groupMaps,
    isLoading,
    setIsLoading,
    nameErrorMessages,
    descriptionErrorMessages,
    targetFieldsError,
    rulesError,
    validateFormData,
    auth,
    history,
    fetchGroupMaps,

    handleCancel,
    handleDeleteRule,
    handleSaveRule,
    handleSaveField,
    handleDeleteField,
    isTargetFieldUsedInRules,
  };
};
